import { Box, Stack, Typography } from '@mui/material';
import { TachographDataDuringFromToPeriod } from './TachographService';
import { useContext } from 'react';
import { ctxTachographDiagram } from '@/contexts/TachographDiagramCtx';
import { dateDot } from '@/utilities/dateUtils';
import { useTranslation } from 'react-i18next';
import { ChevronRight, CreditCardOff } from '@mui/icons-material';
import { timeDurationShort } from '@/utilities/timeUtils';
import { colorsFromNumber, icons } from './TachographDiagramTypes';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';

export default function TachographDiagramSummary({
  tachographProcessedData,
}: {
  tachographProcessedData: TachographDataDuringFromToPeriod[] | undefined;
}) {
  const diagramCtx = useContext(ctxTachographDiagram);
  const { t } = useTranslation();

  if (!diagramCtx || !diagramCtx.selectedDates) return;

  const fromDate = diagramCtx.selectedDates.from;
  const toDate = diagramCtx.selectedDates.to;
  const summaryByEach = tachographProcessedData?.reduce(
    (total, x) => ({
      restTimeMillis: total.restTimeMillis + (x.summary?.restTimeMillis ?? 0),
      availabilityTimeMillis: total.availabilityTimeMillis + (x.summary?.availabilityTimeMillis ?? 0),
      workTimeMillis: total.workTimeMillis + (x.summary?.workTimeMillis ?? 0),
      drivingTimeMillis: total.drivingTimeMillis + (x.summary?.drivingTimeMillis ?? 0),
      noCardTimeMillis: total.noCardTimeMillis + (x.summary?.noCardTimeMillis ?? 0),
    }),
    {
      drivingTimeMillis: 0,
      workTimeMillis: 0,
      restTimeMillis: 0,
      availabilityTimeMillis: 0,
      noCardTimeMillis: 0,
    }
  ) ?? {
    drivingTimeMillis: 0,
    workTimeMillis: 0,
    restTimeMillis: 0,
    availabilityTimeMillis: 0,
    noCardTimeMillis: 0,
  };

  const noCardDays =
    tachographProcessedData?.reduce((total, val) => {
      if (val.states !== undefined) return total;

      const from = val.fromDate;
      const to = val.toDate;
      const isSameDay =
        from.getFullYear() === to.getFullYear() && from.getMonth() === to.getMonth() && from.getDate() === to.getDate();

      if (isSameDay) return total - 86_400_000;

      return total + (to.getTime() - from.getTime() - 86_400_000);
    }, 0) ?? 0;

  const summaryAllTime =
    summaryByEach.availabilityTimeMillis +
    summaryByEach.drivingTimeMillis +
    summaryByEach.restTimeMillis +
    summaryByEach.workTimeMillis +
    summaryByEach.noCardTimeMillis +
    -noCardDays;

  const summaryKeys = [
    'noCardTimeMillis',
    'restTimeMillis',
    'availabilityTimeMillis',
    'workTimeMillis',
    'drivingTimeMillis',
  ] as const;

  const yIconFor = (raw: number) => {
    const svg = icons[raw];
    return svg ? { svg, size: 20 } : null;
  };

  const ticks = [4, 3, 2, 1, 0];

  const noCardTime =
    summaryByEach.noCardTimeMillis !== 0
      ? timeDurationShort(t, (-noCardDays + summaryByEach.noCardTimeMillis) * 1000, undefined, true, true)
      : 0 + t('general.dateTime.minute');

  const dateAndSum = (
    <>
      <Typography color="primary.light" data-qa-element-id="tachograph_selected_date_interval">
        {dateDot(fromDate, toDate, t)}.:
      </Typography>
      <Typography
        variant="subtitle1"
        color="primary"
        sx={{ px: '5px', lineHeight: 1.5 }}
        data-qa-element-id="tachograph_summary_time"
      >
        {timeDurationShort(t, summaryAllTime * 1000, undefined, true)}
      </Typography>
    </>
  );

  const separateTimes = (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
      {ticks.map((v) => {
        const spec = yIconFor(v);
        if (!spec) return null;

        const val = summaryByEach[summaryKeys[v]] ?? 0;
        const value =
          val !== 0 ? timeDurationShort(t, val * 1000, undefined, true, true) : 0 + t('general.dateTime.minute');

        return (
          <Stack
            key={v}
            direction="row"
            alignItems="center"
            sx={{ mx: isPhoneOrHighScreen() ? '0px' : '10px' }}
            data-qa-element-id={`summary_panel_for_${summaryKeys[v]}`}
          >
            <img src={`data:image/svg+xml;utf8,${encodeURIComponent(spec.svg)}`} width={20} height={20} />
            <Typography
              variant="caption"
              sx={{ borderRadius: '4px', padding: '2px' }}
              bgcolor={colorsFromNumber[v].bgColor}
              data-qa-element-id={`summary_time_for_${summaryKeys[v]}`}
            >
              {value}
            </Typography>
          </Stack>
        );
      })}

      <Stack
        direction="row"
        alignItems="center"
        sx={{ mx: isPhoneOrHighScreen() ? '0px' : '10px' }}
        data-qa-element-id="summary_panel_for_no_card"
      >
        <CreditCardOff color="disabled" />
        <Typography variant="caption" sx={{ padding: '2px' }} data-qa-element-id="summary_time_for_no_card">
          {noCardTime}
        </Typography>
      </Stack>
    </Box>
  );

  return isPhoneOrHighScreen() ? (
    <Stack direction="column" data-qa-element-id="tachograph_summary_panel">
      <Box sx={{ display: 'flex', alignItems: 'center', my: '5px' }}>{dateAndSum}</Box>
      {separateTimes}
    </Stack>
  ) : (
    <Box sx={{ display: 'flex', alignItems: 'center', py: '10px' }} data-qa-element-id="tachograph_summary_panel">
      {dateAndSum}
      <ChevronRight color="primary" />
      {separateTimes}
    </Box>
  );
}
